// Контроллер захода (E03-T05, E03-T07; tech/04 §8, §10; ux/04 §4). Чистая логика без DOM и Pixi:
// клиент показывает только числа из TurnResult (ADR-0003) и ничего не предсказывает сам.
// Машина состояний tech/04 §8: Ready → Sending → Playing → Ready | Result; Sending → Retrying (0.5/1/2 с,
// таймаут 4 с) → Resync (GET /battles/active). Обрыв дольше — OVL-FIGHT-RECONNECT с отсчётом 60 с.
import { COMBAT_TIMING, animMs, type BattleEvent, type BattleResult, type BattleStart, type BattleState, type MoveId, type TurnResult } from '@garages/contract';
import { ApiCallError, type ApiCall } from '../net/api-client.js';
import { bossInfo, bossMoveName, MOVES } from '../content/catalog.js';
import { num } from '../i18n/format.js';
import { t as tr } from '../i18n/t.js';

export type Phase = 'intro' | 'ready' | 'sending' | 'playing' | 'reconnect' | 'over';
type MoveState = BattleStart['moves'][number];

export interface FightView {
  battleId: string;
  bossId: string;
  mode: BattleStart['boss']['mode'];
  boss: { hp: number; hpMax: number };
  player: { hp: number; hpMax: number };
  moves: MoveState[];
  diesel: number;
  round: number;
  turnNo: number;
  /** Конец выбора хода по часам клиента; null — таймера нет (обучение, ход обрабатывается). */
  choiceEndsAt: number | null;
  phase: Phase;
  /** Ответ не пришёл за 600 мс — над персонажем «…» (ux/04 §4.3). */
  slow: boolean;
  reconnectUntil: number | null;
  telegraph: BattleStart['boss']['telegraph'];
  log: string[];
  autoStreak: number;
  myDamage: number;
  result: BattleResult | null;
  /** Новый приём появился на панели в этом раунде (вспышка «Новый приём»). */
  newMoves: MoveId[];
}

export interface FightDeps {
  call: ApiCall;
  now?: () => number;
  sleep?: (ms: number) => Promise<void>;
  /** Проиграть события на сцене; резолвится по окончании (длительность — anim_ms). */
  play?: (events: BattleEvent[], view: FightView) => Promise<void>;
  /** Обучение T03–T04: таймер выбора скрыт, автоход не шлётся (ux/11 §1.3). */
  noTimer?: boolean;
  onChange?: (v: FightView) => void;
  onHaptic?: (k: 'light' | 'medium' | 'heavy' | 'soft' | 'rigid' | 'error' | 'success' | 'warning') => void;
  /** Время на заставку входа «Бой!» (ux/04 §4.6: 2.0 с). */
  introMs?: number;
}

export const TURN_RETRY_MS = [500, 1000, 2000] as const;
export const SLOW_MS = 600;
export const RECONNECT_S = 60;

export function logLines(events: readonly BattleEvent[], bossId: string, auto = false): string[] {
  const boss = bossInfo(bossId).short;
  const out: string[] = [];
  let move: string | null = null;
  let bossMove: string | null = null;
  for (const e of events) {
    switch (e.e) {
      case 'player_move': move = e.auto || auto ? tr('CL-FIGHT-CONTROLLER-01', { p0: MOVES.strike.name }) : MOVES[e.moveId].name; break;
      case 'hit_boss': out.push(e.miss ? tr('CL-FIGHT-CONTROLLER-02', { p0: move ?? tr('CL-FIGHT-CONTROLLER-HIT') }) : tr('CL-FIGHT-CONTROLLER-03', { p0: move ?? tr('CL-FIGHT-CONTROLLER-HIT'), p1: num(e.dmg), p2: e.crit ? '!' : '' })); break;
      case 'boss_move': bossMove = bossMoveName(e.moveId); break;
      case 'hit_player': out.push(e.dodged ? tr('CL-FIGHT-CONTROLLER-04', { p0: boss, p1: bossMove ?? tr('CL-FIGHT-CONTROLLER-HIT') }) : tr('CL-FIGHT-CONTROLLER-05', { p0: boss, p1: bossMove ?? tr('CL-FIGHT-CONTROLLER-HIT'), p2: num(e.dmg) })); break;
      case 'effect_tick': out.push(e.target === 'boss' ? tr('CL-FIGHT-CONTROLLER-06', { p0: num(e.dmg) }) : tr('CL-FIGHT-CONTROLLER-07', { p0: e.effect, p1: num(e.dmg) })); break;
      case 'heal': out.push(e.target === 'player' ? tr('CL-FIGHT-CONTROLLER-08', { p0: num(e.amount) }) : tr('CL-FIGHT-CONTROLLER-09', { p0: boss, p1: num(e.amount) })); break;
      case 'shield': out.push(tr('CL-FIGHT-CONTROLLER-10', { p0: boss, p1: num(e.amount) })); break;
      case 'telegraph': out.push(tr('CL-FIGHT-CONTROLLER-11', { p0: boss, p1: bossMoveName(e.moveId) })); break;
      case 'boss_skip': out.push(tr('CL-FIGHT-CONTROLLER-12', { p0: boss })); break;
      case 'boss_gone': out.push(tr('CL-FIGHT-CONTROLLER-13', { p0: boss, p1: e.by.name })); break;
      default: break;
    }
  }
  return out;
}

export class FightController {
  v: FightView;
  private now: () => number;
  private sleep: (ms: number) => Promise<void>;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private disposed = false;

  constructor(private deps: FightDeps, init: BattleStart | BattleState, o: { restored?: boolean } = {}) {
    this.now = deps.now ?? Date.now;
    this.sleep = deps.sleep ?? ((ms) => new Promise((r) => setTimeout(r, ms)));
    const last = 'lastTurn' in init ? init.lastTurn : null;
    this.v = {
      battleId: init.battleId, bossId: init.boss.bossId, mode: init.boss.mode,
      boss: { hp: last?.state.boss.hp ?? init.boss.hp, hpMax: init.boss.hpMax },
      player: { hp: last?.state.player.hp ?? init.player.hp, hpMax: init.player.hpMax },
      moves: last?.state.moves ?? init.moves, diesel: last?.state.diesel ?? init.diesel,
      round: init.round, turnNo: last?.turnNo ?? init.turnNo,
      choiceEndsAt: null, phase: o.restored ? 'ready' : 'intro', slow: false, reconnectUntil: null,
      telegraph: init.boss.telegraph, log: [], autoStreak: 0, myDamage: 0, result: last?.over ?? null, newMoves: [],
    };
  }

  /** Старт: заставка «Бой!» 2 с (или восстановление без анимации прошедших ходов — tech/04 §10 п. 2). */
  async begin(serverDeadlineAt: number, openingEvents: BattleEvent[] = []): Promise<void> {
    if (this.v.result) { this.v.phase = 'over'; this.emit(); return; }
    if (this.v.phase === 'intro') {
      this.emit();
      await this.sleep(this.deps.introMs ?? 2000);
      if (openingEvents.length) await this.deps.play?.(openingEvents, this.v);
    }
    this.toReady(serverDeadlineAt);
  }

  private emit() { if (!this.disposed) this.deps.onChange?.({ ...this.v }); }

  dispose(): void {
    this.disposed = true;
    if (this.timer) clearTimeout(this.timer);
  }

  /** Таймер выбора 4.5 с от конца анимации, но не позже срока сервера минус допуск сети (tech/04 §4.3). */
  private toReady(serverDeadlineAt: number) {
    this.v.phase = 'ready';
    this.v.slow = false;
    if (this.deps.noTimer) {
      this.v.choiceEndsAt = null;
    } else {
      const local = this.now() + COMBAT_TIMING.choiceTimerMs;
      const server = serverDeadlineAt - COMBAT_TIMING.turnGraceMs;
      this.v.choiceEndsAt = Math.min(local, Math.max(server, this.now() + 1000));
      if (this.timer) clearTimeout(this.timer);
      this.timer = setTimeout(() => { void this.choose('strike', true); }, this.v.choiceEndsAt - this.now());
    }
    this.emit();
  }

  canUse(m: MoveState): 'ok' | 'cooldown' | 'diesel' | 'busy' {
    if (this.v.phase !== 'ready') return 'busy';
    if (!m.ready || m.cooldown > 0) return 'cooldown';
    if (m.dieselCost > this.v.diesel) return 'diesel';
    return 'ok';
  }

  /** Тап по приёму или истёк таймер. Повторный тап во время обработки игнорируется. */
  async choose(moveId: MoveId, auto = false): Promise<void> {
    if (this.v.phase !== 'ready' || this.disposed) return;
    const m = this.v.moves.find((x) => x.moveId === moveId);
    if (!m) return;
    const can = this.canUse(m);
    if (can === 'cooldown') { this.deps.onHaptic?.('error'); return; }
    if (can === 'diesel') return;
    if (this.timer) clearTimeout(this.timer);
    this.deps.onHaptic?.('light');
    this.v.phase = 'sending';
    this.v.choiceEndsAt = null;
    this.v.autoStreak = auto ? this.v.autoStreak + 1 : 0;
    this.emit();
    const turnNo = this.v.turnNo + 1;
    const slowTimer = setTimeout(() => { if (this.v.phase === 'sending') { this.v.slow = true; this.emit(); } }, SLOW_MS);
    try {
      const tr = await this.sendTurn(turnNo, moveId, auto);
      clearTimeout(slowTimer);
      if (tr) await this.applyTurn(tr, auto);
    } finally {
      clearTimeout(slowTimer);
    }
  }

  /** Повтор того же turnNo и moveId 3 раза (0.5, 1, 2 с), затем OVL-FIGHT-RECONNECT. */
  private async sendTurn(turnNo: number, moveId: MoveId, auto: boolean): Promise<TurnResult | null> {
    for (let attempt = 0; ; attempt++) {
      try {
        return await this.deps.call('battleTurn', {
          params: { battleId: this.v.battleId },
          body: { turnNo, moveId, auto, clientTs: this.now() },
          noRetry: true,
          timeoutMs: COMBAT_TIMING.clientTurnTimeoutMs,
        });
      } catch (e) {
        if (!(e instanceof ApiCallError)) throw e;
        if (e.code === 'STALE_TURN') { await this.resync(e.details?.['state'] as BattleState | undefined); return null; }
        if (e.code === 'BATTLE_OVER' || e.code === 'NOT_FOUND') { await this.loadResult(); return null; }
        if (e.code === 'COOLDOWN' || e.code === 'NOT_ENOUGH_DIESEL') { await this.resync(); return null; }
        const transient = e.code === 'NETWORK' || e.code === 'TIMEOUT' || e.code === 'INTERNAL' || e.code === 'UNAVAILABLE' || e.code === 'RATE_LIMITED';
        if (!transient) throw e;
        if (attempt < TURN_RETRY_MS.length) { await this.sleep(TURN_RETRY_MS[attempt]!); continue; }
        await this.reconnect();
        return null;
      }
    }
  }

  private async applyTurn(tr: TurnResult, auto: boolean) {
    this.v.phase = 'playing';
    this.v.slow = false;
    const prevIds = new Set(this.v.moves.map((m) => m.moveId));
    this.v.log = [...this.v.log, ...logLines(tr.events, this.v.bossId, auto)].slice(-60);
    for (const e of tr.events) if (e.e === 'hit_boss') this.v.myDamage += e.effective;
    const tg = tr.events.find((e): e is Extract<BattleEvent, { e: 'telegraph' }> => e.e === 'telegraph');
    this.v.telegraph = tg ? { moveId: tg.moveId, nextRound: tg.nextRound } : null;
    this.emit();
    await this.deps.play?.(tr.events, this.v);
    if (this.disposed) return;
    const wasReady = new Set(this.v.moves.filter((m) => m.ready).map((m) => m.moveId));
    this.v.boss = { hp: tr.state.boss.hp, hpMax: tr.state.boss.hpMax };
    this.v.player = { ...this.v.player, hp: tr.state.player.hp };
    this.v.moves = tr.state.moves;
    this.v.newMoves = tr.state.moves.map((m) => m.moveId).filter((id) => !prevIds.has(id));
    if (tr.state.moves.some((m) => m.ready && !wasReady.has(m.moveId) && prevIds.has(m.moveId))) this.deps.onHaptic?.('rigid');
    this.v.diesel = tr.state.diesel;
    this.v.round = tr.round;
    this.v.turnNo = tr.turnNo;
    if (tr.over) { this.finish(tr.over); return; }
    this.toReady(tr.state.turnDeadlineAt);
  }

  private finish(r: BattleResult) {
    if (this.timer) clearTimeout(this.timer);
    this.v.result = r;
    this.v.phase = 'over';
    this.v.reconnectUntil = null;
    this.deps.onHaptic?.(r.outcome === 'boss_defeated' || r.outcome === 'boss_gone' ? 'success' : r.outcome === 'player_defeated' ? 'warning' : 'medium');
    this.emit();
  }

  /** Восстановление экрана из BattleState без анимации прошедших ходов (tech/04 §10 п. 2). */
  async resync(state?: BattleState): Promise<void> {
    let st = state;
    if (!st) {
      try {
        st = await this.deps.call('getActiveBattle', { noRetry: true, timeoutMs: COMBAT_TIMING.clientTurnTimeoutMs });
      } catch (e) {
        if (e instanceof ApiCallError && (e.code === 'NETWORK' || e.code === 'TIMEOUT')) { await this.reconnect(); return; }
        throw e;
      }
    }
    if (!st || st.battleId !== this.v.battleId) { await this.loadResult(); return; }
    const last = st.lastTurn;
    this.v.boss = { hp: last?.state.boss.hp ?? st.boss.hp, hpMax: st.boss.hpMax };
    this.v.player = { hp: last?.state.player.hp ?? st.player.hp, hpMax: st.player.hpMax };
    this.v.moves = last?.state.moves ?? st.moves;
    this.v.diesel = last?.state.diesel ?? st.diesel;
    this.v.round = st.round;
    this.v.turnNo = last?.turnNo ?? st.turnNo;
    this.v.reconnectUntil = null;
    if (last?.over) { this.finish(last.over); return; }
    this.toReady(last?.state.turnDeadlineAt ?? st.turnDeadlineAt);
  }

  /** Итог закончившегося захода (tech/04 §10 п. 3). */
  async loadResult(): Promise<void> {
    try {
      const r = await this.deps.call('getBattle', { params: { battleId: this.v.battleId } });
      this.finish(r);
    } catch (e) {
      if (e instanceof ApiCallError && (e.code === 'NETWORK' || e.code === 'TIMEOUT')) { await this.reconnect(); return; }
      throw e;
    }
  }

  /**
   * OVL-FIGHT-RECONNECT: пауза и отсчёт 60 с (сервер ждёт столько же). Связь вернулась раньше — заход
   * продолжается с состояния сервера; позже — показываем BattleResult (tech/04 §10 п. 2–3, ux/04 §4.6).
   */
  async reconnect(): Promise<void> {
    if (this.timer) clearTimeout(this.timer);
    if (this.v.phase === 'reconnect' || this.disposed) return;
    this.v.phase = 'reconnect';
    this.v.choiceEndsAt = null;
    this.v.reconnectUntil = this.now() + RECONNECT_S * 1000;
    this.emit();
    for (;;) {
      if (this.disposed) return;
      await this.sleep(2000);
      try {
        const st = await this.deps.call('getActiveBattle', { noRetry: true, timeoutMs: COMBAT_TIMING.clientTurnTimeoutMs });
        this.v.phase = 'sending';
        if (st && st.battleId === this.v.battleId && this.now() < (this.v.reconnectUntil ?? 0)) { await this.resync(st); return; }
        await this.loadResult();
        return;
      } catch (e) {
        if (!(e instanceof ApiCallError) || (e.code !== 'NETWORK' && e.code !== 'TIMEOUT' && e.code !== 'INTERNAL' && e.code !== 'UNAVAILABLE')) throw e;
      }
    }
  }

  /** Приложение свёрнуто посреди захода = обрыв связи (ux/04 §4.6): по возвращении — состояние сервера. */
  async onResume(): Promise<void> {
    if (this.v.phase === 'over' || this.v.phase === 'reconnect') return;
    if (this.timer) clearTimeout(this.timer);
    this.v.phase = 'sending';
    await this.resync();
  }

  onHidden(): void {
    if (this.timer) clearTimeout(this.timer); // таймер выбора на фоне не шлёт ходы: сервер сам доиграет
  }

  /** «Уйти» в MOD-FIGHT-LEAVE (X-20). BATTLE_OVER — сразу итог без сообщений. */
  async retreat(): Promise<void> {
    if (this.timer) clearTimeout(this.timer);
    try {
      const r = await this.deps.call('battleRetreat', { params: { battleId: this.v.battleId }, body: {} });
      this.finish(r);
    } catch (e) {
      if (e instanceof ApiCallError && e.code === 'BATTLE_OVER') { await this.loadResult(); return; }
      throw e;
    }
  }
}

export { animMs };
